/* istanbul ignore file */

/*
 * Encapsulates sending HTTP requests.
 * If we later switch from using Axios, only this file should be updated.
 */

import axios, { AxiosRequestConfig } from 'axios';

export type HttpRequest<T> = {
  url: string;
  acceptHeader: string;
  authorizationHeader?: string;
  contentTypeHeader: string;
  data?: T;
  withCredentials: boolean;
};

export function parseStatusCodeFromAxiosErrorMessage(
  message: string,
): number | null {
  // When an HTTP request returns an error response code,
  // Axios throws an error with a message like "Failed with status code 400"
  const statusCodeRegex = /status code (\d+)/;
  const match = statusCodeRegex.exec(message);
  const statusCode = match?.[1];
  return statusCode ? Number(statusCode) : null;
}

export class HttpError extends Error {
  public statusCode: number | null;

  constructor(message: string) {
    super(message);
    this.statusCode = parseStatusCodeFromAxiosErrorMessage(message);
  }
}

const IP_HOSTNAME_PATTERN = /^(\[.+\]|localhost|\d{1,3}(\.\d{1,3}){3})$/;

const PATH_TRAVERSAL_SEGMENTS = ['..', '%2e%2e', '.%2e', '%2e.'];

function hasPathTraversal(url: string): boolean {
  // new URL() discards tabs and newlines, so ".<TAB>." would otherwise hide a ".." segment from this check
  const [rawPath] = url.replace(/[\t\n\r]/g, '').split(/[?#]/);
  return rawPath
    .split(/[/\\]/)
    .some((segment) => PATH_TRAVERSAL_SEGMENTS.includes(segment.toLowerCase()));
}

export function validateRequestUrl(url: string): string {
  if (!url.startsWith('https://')) {
    throw new HttpError('Request URL must use HTTPS');
  }

  if (hasPathTraversal(url)) {
    throw new HttpError('Request URL must not contain path traversal');
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new HttpError('Request URL is not a valid URL');
  }

  if (IP_HOSTNAME_PATTERN.test(parsed.hostname)) {
    throw new HttpError(
      'Request URL must use a domain name, not an IP address',
    );
  }

  return parsed.href;
}

export async function post<TRequest, TResponse>(
  request: HttpRequest<TRequest>,
): Promise<TResponse> {
  const validatedUrl = validateRequestUrl(request.url);

  const config: AxiosRequestConfig = {
    withCredentials: request.withCredentials,
    timeout: 5000, // milliseconds
    headers: {
      Accept: request.acceptHeader,
      Authorization: request.authorizationHeader,
      'Content-Type': request.contentTypeHeader,
    },
  };

  try {
    const response = await axios.post<TResponse>(
      validatedUrl,
      request.data,
      config,
    );

    return response.data;
  } catch (e) {
    throw new HttpError(e.message);
  }
}
